import { type ReactNode, memo } from 'react';
import { Badge, Button } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import splitStyles from '@/components/layout/SplitScreenLayout/SplitScreenLayout.module.css';
import type { GetInvitationByTokenResponse } from '@/types/api/responses';
import styles from '../InvitationPage.module.css';

interface InvitationDetailsStepProps {
  invitation: GetInvitationByTokenResponse;
  isAccepting: boolean;
  isDeclining: boolean;
  onAccept: () => void;
  onDecline: () => void;
}

const CHECK_ICON_SIZE = 18;

export const InvitationDetailsStep = memo(function InvitationDetailsStep({
  invitation,
  isAccepting,
  isDeclining,
  onAccept,
  onDecline,
}: InvitationDetailsStepProps): ReactNode {
  const { t } = useTranslation();

  return (
    <div className={styles.rightFormWrapper}>
      <h2
        className={`${styles.formTitle} ${splitStyles.fadeElement} ${splitStyles.fadeDelay2}`}
      >
        {t('invitation.details.title')}
      </h2>
      <p
        className={`${styles.formSubtitle} ${splitStyles.fadeElement} ${splitStyles.fadeDelay2}`}
      >
        {t('invitation.details.subtitle')}
      </p>

      <div
        className={`${styles.detailCard} ${splitStyles.fadeElement} ${splitStyles.fadeDelay3}`}
      >
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>
            {t('invitation.details.workspace')}
          </span>
          <span className={styles.detailValue}>{invitation.workspaceName}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>
            {t('invitation.details.yourRole')}
          </span>
          <Badge variant="light" size="sm">
            {invitation.assignedRole}
          </Badge>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>
            {t('invitation.details.email')}
          </span>
          <span className={styles.detailValue}>{invitation.email}</span>
        </div>
      </div>

      <div
        className={`${styles.buttonGroup} ${splitStyles.fadeElement} ${splitStyles.fadeDelay4}`}
      >
        <Button
          size="md"
          fullWidth
          leftSection={<IconCheck size={CHECK_ICON_SIZE} />}
          loading={isAccepting}
          disabled={isDeclining}
          onClick={onAccept}
        >
          {isAccepting
            ? t('invitation.actions.accepting')
            : t('invitation.actions.accept')}
        </Button>
        <Button
          size="md"
          fullWidth
          variant="subtle"
          loading={isDeclining}
          disabled={isAccepting}
          onClick={onDecline}
        >
          {isDeclining
            ? t('invitation.actions.declining')
            : t('invitation.actions.decline')}
        </Button>
      </div>
    </div>
  );
});
